import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MatMomentDateModule } from '@angular/material-moment-adapter';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatDateFormats } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  PreloadAllModules,
  provideRouter,
  withComponentInputBinding,
  withPreloading,
  withRouterConfig,
} from '@angular/router';

import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { environment } from '../environments/environment';
import { globalRoutes } from '../global.routes';
import { mpsFetchBearer } from './initializers/mps-oauth2.initializer';
import { mpsFetchUser } from './initializers/mps-user.initializer';
import { mpsLoaderInterceptor } from './interceptors/mps-loader.interceptor';
import { mpsTokenInterceptor } from './interceptors/mps-token.interceptor';
import { MpsUnauthenticatedInterceptor } from './interceptors/mps-unauthenticated.interceptor';
import { MpsAuditInterceptor } from './mpsaudit/interceptors/mps-audit.interceptor';
import { MpsOauth2Service } from './services/mps-oauth2.service';
import { MpsUserService } from './services/mps-user.service';

//import { MatPaginatorIntl } from '@angular/material/paginator';
//import { MpsPaginatorIntl } from './mps-kit/shared/services/mps-paginator-intl.service';

import { HpcTemplatesDataService } from '@app/templates/hpc-templates-data.service';
import { MpsTemplatesDataService } from './mps-kit/shared/services/mps-templates-data.service';

export const FR_DATE_FORMAT: MatDateFormats = {
  parse: {
    dateInput: 'MM-DD-YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

// Local purpose
export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export function httpTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, environment.api.host + '/lokalise/', '.json');
}

export const mpsConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      // Angular packages
      BrowserModule,
      MatDatepickerModule,
      MatMomentDateModule,
      // Plugins packages
      TranslateModule.forRoot({
        defaultLanguage: environment.defaultLocale,
        loader: {
          provide: TranslateLoader,
          // TODO MPS: Change to "httpTranslateLoader" if you want to retrieve translation files for server
          useFactory: createTranslateLoader,
          deps: [HttpClient],
        },
      })
    ),
    provideHttpClient(withInterceptors([mpsTokenInterceptor]), withInterceptorsFromDi()),
    provideAnimationsAsync(),
    { provide: HTTP_INTERCEPTORS, useClass: mpsLoaderInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: MpsUnauthenticatedInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: MpsAuditInterceptor, multi: true },
    { provide: 'OAUTH2_PROVIDERS_URL', useValue: environment.api.providers },
    { provide: 'API_BASE_URL', useValue: environment.api.host },
    { provide: 'LOADER_ON_HTTP', useValue: environment.loaderOnHttp },
    provideAppInitializer(() => {
      const initializerFn = mpsFetchBearer(inject(MpsOauth2Service), inject(MpsUserService));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = mpsFetchUser(inject(MpsUserService));
      return initializerFn();
    }),
    MatDatepickerModule,
    MatMomentDateModule,
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    { provide: MAT_DATE_LOCALE, useValue: 'fr-FR' },
    { provide: MAT_DATE_FORMATS, useValue: FR_DATE_FORMAT },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { disableClose: true, width: '50vw', minWidth: '800px' },
    },
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 5000 } },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline', floatLabel: 'auto' },
    },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        showDelay: 250,
        position: 'above',
        disableTooltipInteractivity: true,
      },
    },
    provideRouter(
      globalRoutes,
      // withDebugTracing(), // Uncomment the following line to enable routing debug trace
      withComponentInputBinding(),
      withRouterConfig({
        onSameUrlNavigation: 'reload',
      }),
      withPreloading(PreloadAllModules)
    ),
    provideAnimations(),

    //@MPS-KIT::TODO :: chose your MpsTemplatesDataService
    {
      provide: MpsTemplatesDataService,
      useClass: HpcTemplatesDataService,
    },
    // { provide: MatPaginatorIntl, useClass: MpsPaginatorIntl }, // Use the custom paginator
  ],
};
